<script lang="ts">
  // One buyer's pages: what exists, what is live, and the editor.
  //
  // A page is created as a DRAFT and a draft is invisible to the buyer. Staff can work
  // on a price list for a week without anything appearing on anyone's phone; only
  // publishing moves the counter the buyer's app watches. Even then nothing is pushed
  // and no device is woken — the buyer sees it the next time they look.
  //
  // The preview below uses the SAME renderer the buyer gets. An editor that previews
  // through different code is an editor that lies.

  import { t } from '$lib/i18n/index.svelte';
  import { asClientError } from '$lib/protocol/errors';
  import {
    operatorDeleteView,
    operatorListViews,
    operatorPublishView,
    operatorSaveView,
    type StaffView,
  } from '$lib/protocol/operator-api';
  import { parseElements, type MessageElement } from '$lib/protocol/types';
  import { loadStaffState, staffServerUrl, staffSession } from '$lib/state/staff.svelte';

  import { goBack, navigate } from '../../../app/router.svelte';
  import type { ElementContext } from '../../elements/context';
  import ElementView from '../../elements/ElementView.svelte';

  let { threadId }: { threadId: string } = $props();

  let views = $state<StaffView[]>([]);
  let problem = $state('');
  let busy = $state(true);

  // The page being composed. Elements are edited as JSON: the vocabulary is the
  // protocol's own, and a form per element type would be a second, drifting definition
  // of it. The preview is what makes this workable, and the server validates on save.
  let editingId = $state<string | undefined>(undefined);
  let draftTitle = $state('');
  let draftJson = $state('[\n  { "type": "text", "text": "" }\n]');
  let creating = $state(false);

  async function reload(): Promise<void> {
    const session = staffSession();
    if (session === undefined) {
      navigate({ name: 'staffLogin' });
      return;
    }
    busy = true;
    try {
      views = await operatorListViews({
        serverUrl: staffServerUrl(),
        token: session.token,
        threadId,
      });
      problem = '';
    } catch (error) {
      problem = asClientError(error).message;
    } finally {
      busy = false;
    }
  }

  $effect(() => {
    void loadStaffState().then(reload);
  });

  const parsedDraft = $derived.by<{ elements: MessageElement[]; raw: unknown[]; error: string }>(
    () => {
      try {
        const raw: unknown = JSON.parse(draftJson);
        if (!Array.isArray(raw)) {
          return { elements: [], raw: [], error: t('staff.jsonArray') };
        }
        return { elements: parseElements(raw), raw, error: '' };
      } catch (error) {
        return { elements: [], raw: [], error: (error as Error).message };
      }
    },
  );

  // Nothing in a preview may act: a preview is a picture of what the buyer will see, and
  // a button that worked here would act on the STAFF member's session.
  const previewCtx: ElementContext = {
    accountId: '',
    sequence: 0,
    disabled: true,
    answeredQuestions: new Set<string>(),
    staleCarts: new Set<string>(),
    mutedCategories: new Set<string>(),
    resolveCart: () => undefined,
    resolveEvent: () => undefined,
    onAnswer: () => undefined,
    onRequestItems: () => undefined,
    onCartRespond: () => undefined,
  };

  function edit(view: StaffView): void {
    editingId = view.viewId;
    creating = false;
    draftTitle = view.title;
    draftJson = JSON.stringify(view.rawElements, null, 2);
  }

  function startNew(): void {
    editingId = undefined;
    creating = true;
    draftTitle = '';
    draftJson = '[\n  { "type": "text", "text": "" }\n]';
  }

  async function withSession(work: (token: string, serverUrl: string) => Promise<void>): Promise<void> {
    const session = staffSession();
    if (session === undefined) {
      navigate({ name: 'staffLogin' });
      return;
    }
    try {
      await work(session.token, staffServerUrl());
      problem = '';
    } catch (error) {
      // The server rejects a page on the way in — a tag outside the subset, a slot with
      // no control, an address anywhere. Showing that here is the point: staff find out
      // while they are still looking at what they composed.
      problem = asClientError(error).message;
    }
  }

  async function save(): Promise<void> {
    if (parsedDraft.error.length > 0) {
      problem = parsedDraft.error;
      return;
    }
    await withSession(async (token, serverUrl) => {
      const saved = await operatorSaveView({
        serverUrl,
        token,
        threadId,
        ...(editingId !== undefined ? { viewId: editingId } : {}),
        title: draftTitle,
        elements: parsedDraft.raw,
      });
      editingId = saved.viewId;
      creating = false;
    });
    await reload();
  }

  async function publish(viewId: string): Promise<void> {
    await withSession(async (token, serverUrl) => {
      await operatorPublishView({ serverUrl, token, threadId, viewId });
    });
    await reload();
  }

  async function remove(viewId: string): Promise<void> {
    await withSession(async (token, serverUrl) => {
      await operatorDeleteView({ serverUrl, token, threadId, viewId });
    });
    if (editingId === viewId) {
      editingId = undefined;
      creating = false;
    }
    await reload();
  }

  const publishedCount = $derived(views.filter((v) => v.published).length);
</script>

<header class="bar">
  <button class="back" onclick={goBack} aria-label={t('common.back')}>‹</button>
  <h1>{t('staff.pages')}</h1>
</header>

{#if problem.length > 0}
  <p class="problem" role="alert">{problem}</p>
{/if}

<div class="scroll">
  <section>
    <p class="count">{t('staff.published', { n: publishedCount })}</p>
    {#if busy}
      <p class="hint">{t('views.loading')}</p>
    {/if}
    <ul>
      {#each views as view (view.viewId)}
        <li>
          <span class="name">{view.title}</span>
          <span class="badge">{view.published ? t('staff.live') : t('staff.draft')}</span>
          <span class="actions">
            <button onclick={() => edit(view)}>{t('staff.edit')}</button>
            <button onclick={() => void publish(view.viewId)} disabled={view.published && view.version > 0}>
              {t('staff.publish')}
            </button>
            <button onclick={() => void remove(view.viewId)}>{t('staff.remove')}</button>
          </span>
        </li>
      {/each}
    </ul>
    <button class="new" onclick={startNew}>{t('staff.newPage')}</button>
  </section>

  {#if creating || editingId !== undefined}
    <section class="editor">
      <label>
        {t('staff.pageTitle')}
        <input type="text" bind:value={draftTitle} maxlength="60" />
      </label>
      <label>
        {t('staff.elements')}
        <textarea bind:value={draftJson} rows="12" spellcheck="false"></textarea>
      </label>
      <p class="hint">{t('staff.slotHint')}</p>
      {#if parsedDraft.error.length > 0}
        <p class="problem">{parsedDraft.error}</p>
      {/if}
      <button class="new" onclick={() => void save()}>{t('staff.save')}</button>

      <p class="count">{t('staff.preview')}</p>
      <div class="preview">
        {#each parsedDraft.elements as element, i (i)}
          <div class="block"><ElementView {element} ctx={previewCtx} /></div>
        {/each}
      </div>
    </section>
  {/if}
</div>

<style>
  .bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
  }
  .back {
    border: 0;
    background: transparent;
    font-size: 28px;
    cursor: pointer;
    color: var(--ink);
    padding: 0 8px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  .scroll {
    overflow-y: auto;
    padding: 0 16px 24px;
  }
  ul {
    list-style: none;
    margin: 0 0 12px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface);
    padding: 10px 12px;
  }
  .name {
    font-weight: 600;
  }
  .badge {
    font-size: 12px;
    color: var(--ink-soft);
  }
  .actions {
    margin-left: auto;
    display: flex;
    gap: 6px;
  }
  .actions button,
  .new {
    border: 1px solid var(--line);
    border-radius: 8px;
    background: transparent;
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    padding: 6px 10px;
    cursor: pointer;
  }
  .new {
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 600;
  }
  .editor {
    margin-top: 20px;
    border-top: 1px solid var(--line);
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    color: var(--ink-soft);
  }
  input,
  textarea {
    border: 1px solid var(--line);
    border-radius: 8px;
    background: transparent;
    color: var(--ink);
    font: inherit;
    padding: 10px;
  }
  textarea {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
  }
  .preview {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .block {
    border: 1px solid var(--line);
    border-radius: var(--radius);
    background: var(--surface);
    padding: 12px;
  }
  .count {
    margin: 0;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ink-soft);
  }
  .problem {
    margin: 0;
    color: var(--danger, #b3261e);
    font-size: 13px;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--ink-soft);
  }
</style>
